/**
 * Evidence Number Format input + preview.
 *
 * Extracted from AdminSettingsPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import type { OrgCaseDefaults, UpdateOrgCaseDefaultsRequest } from '../../types/case-container'
import { generateFormatPreview } from './helpers'

interface EvidenceFormatFieldProps {
  orgDefaults: OrgCaseDefaults | null
  formData: UpdateOrgCaseDefaultsRequest
  setFormData: (data: UpdateOrgCaseDefaultsRequest) => void
  editMode: boolean
}

export function EvidenceFormatField({
  orgDefaults,
  formData,
  setFormData,
  editMode,
}: EvidenceFormatFieldProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-stone-700 dark:text-stone-300">
        {t('adminSettings.evidenceNumberFormat')}
      </h4>
      {editMode ? (
        <EditFormatInput formData={formData} setFormData={setFormData} />
      ) : (
        <ReadOnlyFormat orgDefaults={orgDefaults} />
      )}
    </div>
  )
}

function EditFormatInput({
  formData,
  setFormData,
}: Pick<EvidenceFormatFieldProps, 'formData' | 'setFormData'>): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="space-y-2">
      <input
        type="text"
        value={formData.evidenceNumberFormat ?? ''}
        onChange={(e) => {
          setFormData({ ...formData, evidenceNumberFormat: e.target.value || null })
        }}
        className="w-full rounded-lg border border-stone-300 bg-white px-3 py-2 font-mono text-sm text-stone-900 dark:border-stone-600 dark:bg-stone-700 dark:text-stone-100"
      />
      {formData.evidenceNumberFormat && (
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {t('adminSettings.preview')}{' '}
          <span className="font-mono">{generateFormatPreview(formData.evidenceNumberFormat)}</span>
        </p>
      )}
      <p className="text-xs text-stone-500 dark:text-stone-400">
        {t('adminSettings.tokens')} {'{case_ref}'}, {'{location}'}, {'{node_id}'}, {'{sequence:02}'}
        , {'{date:YYYYMMDD}'}, {'{year}'}
      </p>
    </div>
  )
}

function ReadOnlyFormat({
  orgDefaults,
}: Pick<EvidenceFormatFieldProps, 'orgDefaults'>): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div>
      <p className="rounded bg-stone-100 px-3 py-2 font-mono text-sm text-stone-900 dark:bg-stone-800 dark:text-stone-100">
        {orgDefaults?.evidenceNumberFormat ?? t('adminSettings.notSet')}
      </p>
      {orgDefaults?.evidenceNumberFormat && (
        <p className="mt-1 text-xs text-stone-500 dark:text-stone-400">
          {t('adminSettings.preview')}{' '}
          <span className="font-mono">
            {generateFormatPreview(orgDefaults.evidenceNumberFormat)}
          </span>
        </p>
      )}
    </div>
  )
}
